/**
 * SINCE YOU LAST CHECKED.
 *
 * The best idea in the four reference boards he sent, and the only one of them that is a FEATURE
 * rather than a finish. Every other panel on this deck answers "what is true now". This one answers
 * "what moved", which is the actual reason a person opens a dashboard they already know the shape
 * of. Nothing else on the board answered it: the same facts existed inside the Today panel's update
 * list, three clicks down and undated.
 *
 * WHAT MAKES IT HONEST. Two things, and both are the difference between this and a decorative
 * ticker.
 *
 * One: it says HOW LONG. A change with no age is a change he cannot triage, and "Red Sea reached,
 * 25 days waiting" is the line in his own mockup that carries the whole idea. So every row is
 * stamped, and the stamp is the loudest thing after the title.
 *
 * Two: it never invents a bucket. The strip shows what the payload actually carries and says
 * plainly when nothing has moved, rather than padding to a fixed four columns. A row of empty
 * slots reading "0 new commitments" teaches him to stop looking.
 */

import { Icon, type IconName } from './Icon.js';
import type { DashboardUpdate } from '@rbv/shared';
import './commandDeck.css';

/** Each update kind's icon and the word for it. The vocabulary is the vault's, not a new one. */
const KIND: Record<DashboardUpdate['kind'], { icon: IconName; label: string }> = {
  person: { icon: 'waiting', label: 'Person' },
  org: { icon: 'holdings', label: 'Account' },
  topic: { icon: 'project', label: 'Topic' },
  commitment: { icon: 'owed', label: 'Commitment' },
  decision: { icon: 'judgement', label: 'Decision' },
  thread: { icon: 'inbox', label: 'Correspondence' },
  objective: { icon: 'objective', label: 'Objective' },
  meeting: { icon: 'agenda', label: 'Meeting' },
  post: { icon: 'share', label: 'Published' },
};

/**
 * How long ago, in the shortest form that is still true.
 *
 * Deliberately coarse past a day: "3d" is actionable and "3d 4h" is noise. An unparseable or
 * future stamp returns null and the row simply carries no age, rather than showing "NaN" or
 * quietly pretending it happened now.
 */
export function ageLabel(at: string, now: number): string | null {
  const t = Date.parse(at);
  if (Number.isNaN(t)) return null;
  const mins = Math.floor((now - t) / 60000);
  if (mins < 0) return null;
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 14) return `${days}d`;
  return `${Math.floor(days / 7)}w`;
}

export function SinceYouChecked({
  updates,
  onOpen,
  onViewAll,
}: {
  updates: DashboardUpdate[];
  onOpen: (pathOrTitle: string) => void;
  onViewAll: () => void;
}): JSX.Element | null {
  /*
   * ABSENT, NOT EMPTY. A strip that renders itself saying "nothing changed" every morning is a
   * strip he stops reading, and it would take a row of the grid to say so.
   */
  if (updates.length === 0) return null;

  const now = Date.now();
  /* Four is what fits one row at the width this strip gets. The count beside the title states the
     total, so the cap is visible rather than silent. */
  const shown = updates.slice(0, 4);

  return (
    <section className="cdeck-since" aria-label="What changed since you last checked">
      <p className="cdeck-since-head">
        <Icon name="update" size={13} />
        Since you last checked
        <span className="cdeck-since-n">{updates.length}</span>
      </p>

      <ul className="cdeck-since-list">
        {shown.map((u) => {
          const meta = KIND[u.kind];
          const age = ageLabel(u.at, now);
          return (
            <li key={u.path} className="cdeck-since-item">
              <button type="button" className="cdeck-since-btn" onClick={() => onOpen(u.path)}>
                <span className="cdeck-since-ico">
                  <Icon name={meta.icon} size={14} />
                </span>
                <span className="cdeck-since-text">
                  <span className="cdeck-since-kind">{meta.label}</span>
                  <span className="cdeck-since-title">{u.title}</span>
                </span>
                {/* The age is the point of the row, so it is not a footnote. */}
                {age !== null && <span className="cdeck-since-age">{age}</span>}
              </button>
            </li>
          );
        })}
      </ul>

      <button type="button" className="cdeck-since-all" onClick={onViewAll}>
        View changes
        <Icon name="chevron" size={12} />
      </button>
    </section>
  );
}
